import { config } from "./config";

export const normalizeHostname = (input: string): string =>
  input.trim().toLowerCase().replace(/:\d+$/, "").replace(/\.+$/, "");

export const subdomainFromHostname = (
  hostname: string,
  rootDomain: string,
): string | null => {
  const suffix = `.${rootDomain}`;
  if (!hostname.endsWith(suffix)) return null;
  const subdomain = hostname.slice(0, -suffix.length);
  return subdomain === "" || subdomain.includes(".") ? null : subdomain;
};

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

// Same rule as the sites.subdomain check constraint: a single DNS label of
// lowercase letters, digits and hyphens.
export const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

// Subdomains that can never resolve to a published site: `isSiteHostname`
// treats www.{root} as the app, and my.{root} is reserved for the
// Cloudflare for SaaS fallback origin (HAZ-38; `sites` was the previous
// fallback label and stays reserved), and media.{root} serves uploaded
// images (worker/media.ts). Enforced server-side by the
// sites_subdomain_not_reserved check constraint — keep both lists in sync.
export const RESERVED_SUBDOMAINS = new Set(["www", "sites", "my", "media"]);

// Turns a site name into an address to offer ("Café Noël" -> "cafe-noel").
// Returns "" when nothing usable is left, or the result could never be used.
export const suggestSubdomain = (name: string): string => {
  const label = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63)
    .replace(/-+$/, "");
  return SUBDOMAIN_PATTERN.test(label) && !RESERVED_SUBDOMAINS.has(label)
    ? label
    : "";
};

// Accepts what a user might paste — a bare label, or a full URL/hostname like
// https://mysite.hazumu.tld/ — and returns the candidate subdomain label.
export const normalizeSubdomainInput = (
  input: string,
  rootDomain: string,
): string => {
  const withoutScheme = input.trim().replace(/^https?:\/\//, "");
  const hostname = normalizeHostname(withoutScheme.split("/")[0] ?? "");
  const suffix = `.${rootDomain}`;
  return hostname.endsWith(suffix)
    ? hostname.slice(0, -suffix.length)
    : hostname;
};

// Common two-part public suffixes where a three-label hostname is still apex
// (example.co.uk). Heuristic only — it drives which DNS guidance the UI
// shows. Keep in sync with SECOND_LEVEL_TLDS in
// supabase/functions/manage-domain/index.ts.
const SECOND_LEVEL_TLDS = new Set([
  "ac.uk",
  "co.uk",
  "gov.uk",
  "ltd.uk",
  "me.uk",
  "net.uk",
  "org.uk",
  "plc.uk",
  "com.au",
  "edu.au",
  "gov.au",
  "net.au",
  "org.au",
  "co.nz",
  "net.nz",
  "org.nz",
  "com.ar",
  "com.br",
  "com.cn",
  "com.hk",
  "com.mx",
  "com.sg",
  "com.tw",
  "co.id",
  "co.in",
  "co.jp",
  "co.kr",
  "co.th",
  "co.za",
  "ne.jp",
  "or.jp",
]);

// Same rule as the domains.hostname check constraint.
const DOMAIN_HOSTNAME_PATTERN =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])$/;

// A registrable apex domain (example.com, example.co.uk) cannot hold a CNAME
// at most DNS providers, so apex input gets different guidance than
// www.example.com, where a CNAME works everywhere. Invalid input (e.g.
// underscores) and www.* hostnames are never apex — www is itself a
// subdomain.
export const isApexHostname = (hostname: string): boolean => {
  const normalized = normalizeHostname(hostname);
  if (!DOMAIN_HOSTNAME_PATTERN.test(normalized)) return false;
  if (normalized.startsWith("www.")) return false;
  const labels = normalized.split(".");
  if (labels.length === 2) return true;
  return (
    labels.length === 3 && SECOND_LEVEL_TLDS.has(labels.slice(1).join("."))
  );
};

// The short "name/host" form most DNS providers expect for the record —
// "@" for apex, otherwise the leading labels ("www" for www.example.com).
// The full hostname returned by manage-domain is also accepted by providers.
export const dnsRecordName = (hostname: string): string => {
  const labels = normalizeHostname(hostname).split(".");
  const apexLabels = SECOND_LEVEL_TLDS.has(labels.slice(-2).join(".")) ? 3 : 2;
  return labels.length <= apexLabels
    ? "@"
    : labels.slice(0, -apexLabels).join(".");
};

// Accepts what a user might paste for a custom domain — a bare hostname or a
// full URL — and returns the hostname, matching the normalisation the
// manage-domain edge function applies server-side.
export const normalizeDomainInput = (input: string): string =>
  normalizeHostname(
    input
      .trim()
      .replace(/^https?:\/\//i, "")
      .split("/")[0] ?? "",
  );

export const isSiteHostname = (hostname: string): boolean => {
  const normalized = normalizeHostname(hostname);
  if (!normalized) return false;
  const rootDomain = config.hazumuRootDomain;
  if (normalized === rootDomain || normalized === `www.${rootDomain}`) {
    return false;
  }
  if (subdomainFromHostname(normalized, rootDomain)) return true;
  if (LOCAL_HOSTS.has(normalized) || normalized.endsWith(".localhost")) {
    return false;
  }
  // A bare IP address is never a custom domain; it's the dev server opened
  // from another device on the network (e.g. a phone).
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(normalized) || normalized.includes(":")) {
    return false;
  }
  // Cloudflare Pages and Workers preview deployments run the app, not a
  // published site.
  if (normalized.endsWith(".pages.dev") || normalized.endsWith(".workers.dev")) {
    return false;
  }
  return true;
};
